import { Box, Card, CardContent, Grid, GridSize, Theme, Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { CSSProperties, useEffect, useRef, useState } from "react";
import PagedResult from "../submodules/fox-typescript/core/PagedResult"
import { ConditionalWrapper } from "./Common/ConditionalWrapper";
import InfiniteScrollComponent from "./InfiniteScroll";

export interface CardItemSpec<T> {
  /**
   * Card header content
   */
  header: Spec<T>[];
  /**
   * Card body content
   */
  body: Spec<T>[];
  /**
   * Card footer content
   */
  footer: Spec<T>[];
  /**
   * Conditional property. Callback for the card onClick event
   */
  onClick?: (instance: T) => void;
  /**
   * Conditional property. Custom styles for cards
   */
  customCardStyles?: string;
  /**
   * Conditional property. Custom styles for the cards container
   */
  customContainerStyles?: string;
  /**
   * Conditional property. Size of each element in 12 column grid
   */
  columnSize?: GridSize;
  /**
   * Conditional property. If true render a Grid with container property.
   * If false render a Grid with item property
   */
  multiItemPerRow?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    defaultCard: {
      margin: "1.15em",
      height: "28em",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "10px",
    },
    footer: {
      display: "flex",
      justifyContent: "space-between",
      marginTop: "10px",
    },
    emptyComponent: {
      marginTop: "15px",
    },
  })
);

interface Spec<T> {
  content: (instance: T) => JSX.Element;
}

interface Props<T> {
  /**
   * Function that will be triggered every time that data has to be fetched
   * e.g. when the UI loads, when a page changes, etc.
   */
  fetchData: (page: number, rowsPerPage: number) => Promise<PagedResult<T>>;
  /**
   * Specs for each Item of the card.
   */
  cardItemSpec?: CardItemSpec<T>;
  /**
   * This prop should be updated every time the list of T changed, thus triggering a potential update.
   */
  lastUpdateTimestamp: number;
  /**
   * Text or component to render if list data is empty.
   * Default value is "No data to display"
   */
  listEmptyComponent?: JSX.Element;
  /**
   * Flag to show if it should use infinite scroll component
   */
  useInfiniteScroll?: boolean;
}

const PAGE_SIZE = 20;

function DetailListCrud<T>(props: Props<T>) {
  const [content, setContent] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalElements, setTotalElements] = useState<number>(PAGE_SIZE);
  const [page, setPage] = useState<number>(0);
  const spec = props.cardItemSpec;
  const classes = useStyles();

  useEffect(() => {
    handleFetch();
  }, [props.lastUpdateTimestamp, props.fetchData, page]);

  const columnSize: GridSize = spec?.columnSize ? spec.columnSize : 6;
  const emptyComponent = props.listEmptyComponent || <div style={{ textAlign: "center" }}>No data to display</div>;

  const handleFetch = () => {
    props.fetchData(page, PAGE_SIZE).then((result) => {
      const elementsAreFiltered = totalElements != result.totalElements && content.length !== 0;

      if (elementsAreFiltered) {
        setPage(0);
      }

      setTotalElements(result.totalElements);
      setHasMore(result.totalElements > content.length);
      setContent(elementsAreFiltered ? result.content : [...content, ...result.content]);
    });
  }

  const renderFunction = () => {
    if (content.length === 0) {
      return [(
        <Card variant="outlined" className={`${classes.emptyComponent}`}>
          <CardContent style={{ padding: "1em" }}>{emptyComponent}</CardContent>
        </Card>
      )]
    }

    return content.map((event) => (
      <>
        <ConditionalWrapper
          condition={!!spec?.multiItemPerRow && spec?.multiItemPerRow}
          wrapper={(children) => (
            <Grid item xs={spec?.multiItemPerRow ? columnSize : 12}>
              {children}
            </Grid>
          )}
        >
          <>
            <Card
              variant="outlined"
              className={spec?.customCardStyles ? `${spec?.customCardStyles}` : `${classes.defaultCard}`}
              onClick={() => props.cardItemSpec?.onClick && props.cardItemSpec?.onClick(event)}
            >
              <CardContent>
                {/* Header */}
                <Box className={`${classes.header}`}>
                  {spec?.header.map((item) => (
                    <>{item.content(event)}</>
                  ))}
                </Box>

                {/* Body */}
                <Box>
                  {spec?.body.map((item) => (
                    <>{item.content(event)}</>
                  ))}
                </Box>

                {/* Footer */}
                <Box className={`${classes.footer}`}>
                  {spec?.footer.map((item) => (
                    <>{item.content(event)}</>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </>
        </ConditionalWrapper>
      </>
    ))
  }

  return (
    <> {props.useInfiniteScroll 
    ? <InfiniteScrollComponent
      hasMore={!!props.useInfiniteScroll && hasMore}
      itemsAmount={content.length}
      onFetch={() => setPage((page) => page + 1)}
      renderFunction={renderFunction}
    /> 
    : renderFunction()} 
    </>
  );
}

export default DetailListCrud;
